/**
 * Weekly Deliverables — Per-delivery review board.
 *
 * Responsive image-first review surface. Each ad can be approved or rejected
 * inline; rejections require at least one reason + a comment. Behaviour:
 *
 *  - Approve autosaves IMMEDIATELY (optimistic) via
 *      PUT /api/brands/:brandId/deliveries/:deliveryId/items/:itemId/ads/:itemAdId/decision
 *  - Reject autosaves (debounced) only once reasons + comment + disposition are
 *    complete. An incomplete rejection stays local and blocks submission.
 *  - Optional approval tags/comment update the SAME decision (approval uses the
 *    `comment` field — there is no separate approvalComment).
 *  - Undo DELETEs that same decision endpoint (not just local clear).
 *  - Approve remaining calls POST .../items/:itemId/approve-remaining per item.
 *  - Save failures roll back the optimistic state and toast.
 *  - Reload recovery reads server decisions (item.decisions is flat).
 *  - Item submission does NOT make decisions read-only — only a CLOSED delivery
 *    does. Draft is hidden for non-masters upstream. Submitted state persists
 *    but editing stays allowed until the delivery closes.
 *  - Submit body always includes allowUnanswered and may send decisions:[];
 *    the confirmation targets only the selected item.
 *  - Keyboard: A = approve (autosaves), R = open reject panel (stays until
 *    feedback complete), Arrow Up/Down = navigate, Esc = close.
 *  - Accessible lightbox with zoom, navigation, click-to-pin.
 *  - Removed ads stay visible read-only and are excluded from the denominator.
 *
 * Flag-gated via beta-features (fail-closed).
 */

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { useParams, useLocation, Redirect } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, Eye,
  History, Loader2, MessageSquare, RotateCcw, ThumbsDown,
  ThumbsUp, X, ZoomIn, ZoomOut, CheckCircle2, AlertCircle,
  CalendarClock, SkipForward, Layers, Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdImage } from "@/components/ad-image";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
import {
  DELIVERABLE_REJECT_REASONS, DELIVERABLE_POSITIVE_TAGS,
  validateDeliverableSubmit, resolveAdDecisionForAd,
  type DeliverableRejectReasonKey, type DeliverablePositiveTagKey,
  type DeliverableDisposition, type DeliverableDecisionLike,
} from "@shared/deliverables";
import {
  useWeeklyDeliverablesEnabled,
  type DeliveryDto, type DeliveryItemDto, type DeliveryItemAdDto,
  type DeliveryItemDecisionDto,
} from "./deliverables";

// ── Local (editing) decision state ─────────────────────────────────────────
// A local decision is an in-progress edit for one ad. `saved` = the current
// content has been persisted to the server (a matching decision row exists);
// an unsaved rejection is incomplete and blocks submission. Approval never has
// a separate approvalComment: the optional note lives in `comment`.

type LocalDecision = {
  itemAdId: string;
  decision: "approved" | "rejected";
  reasons: DeliverableRejectReasonKey[];
  comment: string;
  disposition: DeliverableDisposition;
  positiveTags: DeliverablePositiveTagKey[];
  pinX: number | null;
  pinY: number | null;
  saved: boolean;
};

function emptyLocal(itemAdId: string, decision: "approved" | "rejected"): LocalDecision {
  return {
    itemAdId,
    decision,
    reasons: [],
    comment: "",
    disposition: "rework",
    positiveTags: [],
    pinX: null,
    pinY: null,
    saved: false,
  };
}

// Build an editing seed from an existing server decision (for reload recovery
// and when a reviewer re-opens their own saved decision to edit it).
function localFromServer(dec: DeliveryItemDecisionDto): LocalDecision {
  return {
    itemAdId: dec.deliveryItemAdId,
    decision: dec.decision,
    reasons: (dec.reasons ?? []) as DeliverableRejectReasonKey[],
    comment: dec.comment ?? "",
    disposition: (dec.disposition ?? "rework") as DeliverableDisposition,
    positiveTags: (dec.positiveTags ?? []) as DeliverablePositiveTagKey[],
    pinX: dec.pinX ?? null,
    pinY: dec.pinY ?? null,
    saved: true,
  };
}

// A rejection is complete (and therefore savable / submittable) when it has at
// least one reason and a non-empty comment.
function isRejectComplete(local: LocalDecision): boolean {
  return local.reasons.length > 0 && local.comment.trim().length > 0;
}

// Is this local decision in a state that can be persisted?
function isSavable(local: LocalDecision): boolean {
  if (local.decision === "approved") return true;
  return isRejectComplete(local);
}

// Serialize a local decision into the PUT body the server expects. Approval
// carries its optional feedback through `comment` + positiveTags.
function decisionBody(local: LocalDecision) {
  if (local.decision === "approved") {
    return {
      decision: "approved" as const,
      comment: local.comment.trim() || null,
      positiveTags: local.positiveTags,
      reasons: [],
      disposition: null,
      pinX: local.pinX,
      pinY: local.pinY,
    };
  }
  return {
    decision: "rejected" as const,
    reasons: local.reasons,
    comment: local.comment.trim(),
    disposition: local.disposition,
    positiveTags: [],
    pinX: local.pinX,
    pinY: local.pinY,
  };
}

type SubmitDecisionPayload = {
  itemAdId: string;
  decision: "approved" | "rejected";
  reasons: DeliverableRejectReasonKey[];
  comment: string | null;
  disposition: DeliverableDisposition | null;
  positiveTags: DeliverablePositiveTagKey[];
  pinX: number | null;
  pinY: number | null;
};

function submitDecisionBody(local: LocalDecision): SubmitDecisionPayload {
  return { itemAdId: local.itemAdId, ...decisionBody(local) };
}

// ── Filter type ────────────────────────────────────────────────────────────

type ReviewFilter = "all" | "unanswered" | "approved" | "rejected";

// Convert a flat server decision list into the DeliverableDecisionLike shape
// the shared resolver understands.
function toDecisionLike(decs: DeliveryItemDecisionDto[]): DeliverableDecisionLike[] {
  return decs.map((d) => ({
    deliveryItemAdId: d.deliveryItemAdId,
    decision: d.decision,
    reasons: d.reasons,
    comment: d.comment,
    disposition: d.disposition,
  }));
}

// ── Lightbox ───────────────────────────────────────────────────────────────

function Lightbox({
  ads,
  startIndex,
  onClose,
  onPin,
}: {
  ads: DeliveryItemAdDto[];
  startIndex: number;
  onClose: () => void;
  onPin?: (adId: string, x: number, y: number) => void;
}) {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ad = ads[idx];

  const prev = () => { setIdx((i) => Math.max(0, i - 1)); setZoom(1); };
  const next = () => { setIdx((i) => Math.min(ads.length - 1, i + 1)); setZoom(1); };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.25));
      if (e.key === "-") setZoom((z) => Math.max(1, z - 0.25));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onPin || !ad || zoom > 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    onPin(ad.itemAdId, x, y);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl w-full p-0 overflow-hidden bg-black/95 border-0"
        data-testid="dialog-lightbox"
      >
        <DialogTitle className="sr-only">
          {ad?.displayName ?? "Ad preview"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Image {idx + 1} of {ads.length}. Use arrow keys to navigate, Esc to close.
        </DialogDescription>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/80 absolute top-0 left-0 right-0 z-10">
          <span className="text-white/80 text-sm font-medium truncate max-w-xs">
            {ad?.displayName}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-white/50 text-xs">{idx + 1} / {ads.length}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-white hover:text-white hover:bg-white/10"
              onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
              disabled={zoom <= 1}
              aria-label="Zoom out"
              data-testid="button-lightbox-zoom-out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-white hover:text-white hover:bg-white/10"
              onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
              disabled={zoom >= 4}
              aria-label="Zoom in"
              data-testid="button-lightbox-zoom-in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-white hover:text-white hover:bg-white/10"
              onClick={onClose}
              aria-label="Close lightbox"
              data-testid="button-lightbox-close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Image area */}
        <div
          ref={containerRef}
          className={`flex items-center justify-center min-h-[60vh] pt-10 pb-12 px-12 overflow-auto ${zoom > 1 ? "cursor-zoom-out" : onPin ? "cursor-crosshair" : "cursor-default"}`}
          onClick={zoom === 1 ? handleImageClick : undefined}
        >
          {ad?.imageUrl ? (
            <div className="relative inline-block" style={{ transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform 0.15s" }}>
              <img
                ref={imgRef}
                src={ad.imageUrl}
                alt={ad.displayName}
                className="max-h-[70vh] max-w-full block rounded"
                draggable={false}
                data-testid="img-lightbox"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center text-white/40 gap-2">
              <AlertCircle className="h-8 w-8" />
              <span>No image available</span>
            </div>
          )}
        </div>

        {/* Nav arrows */}
        {idx > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 bg-black/40 text-white hover:bg-black/70 rounded-full"
            onClick={prev}
            aria-label="Previous image"
            data-testid="button-lightbox-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        {idx < ads.length - 1 && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 bg-black/40 text-white hover:bg-black/70 rounded-full"
            onClick={next}
            aria-label="Next image"
            data-testid="button-lightbox-next"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        )}

        {/* Thumbnail strip */}
        {ads.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1.5 py-2 bg-black/60 overflow-x-auto px-4">
            {ads.map((a, i) => (
              <button
                key={a.itemAdId}
                onClick={() => { setIdx(i); setZoom(1); }}
                className={`h-10 w-10 rounded overflow-hidden border-2 transition-all shrink-0 ${
                  i === idx ? "border-white" : "border-transparent opacity-50 hover:opacity-80"
                }`}
                aria-label={`View ${a.displayName}`}
                data-testid={`button-lightbox-thumb-${a.itemAdId}`}
              >
                {a.imageUrl ? (
                  <img src={a.imageUrl} alt={a.displayName} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/10" />
                )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Reject form (inline) ───────────────────────────────────────────────────

function RejectForm({
  ad,
  local,
  onChange,
  onClear,
}: {
  ad: DeliveryItemAdDto;
  local: LocalDecision;
  onChange: (patch: Partial<LocalDecision>) => void;
  onClear: () => void;
}) {
  const complete = isRejectComplete(local);

  return (
    <div
      className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-3"
      data-testid={`form-reject-${ad.itemAdId}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-destructive">Rejection details</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
          onClick={onClear}
          aria-label="Cancel rejection"
          data-testid={`button-cancel-reject-${ad.itemAdId}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Reason checkboxes */}
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          What's wrong? <span className="text-destructive">*</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {DELIVERABLE_REJECT_REASONS.map(({ key, label }) => {
            const active = local.reasons.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() =>
                  onChange({
                    reasons: active
                      ? local.reasons.filter((r) => r !== key)
                      : [...local.reasons, key],
                  })
                }
                className={cn(
                  "text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors",
                  active
                    ? "bg-destructive text-destructive-foreground border-destructive"
                    : "bg-background text-muted-foreground border-border hover:border-destructive/50",
                )}
                aria-pressed={active}
                data-testid={`chip-reason-${key}-${ad.itemAdId}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Comment */}
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          Comment <span className="text-destructive">*</span>
        </p>
        <Textarea
          value={local.comment}
          onChange={(e) => onChange({ comment: e.target.value })}
          placeholder="Describe what needs to change…"
          rows={2}
          maxLength={2000}
          className="text-sm resize-none"
          data-testid={`input-reject-comment-${ad.itemAdId}`}
        />
      </div>

      {/* Disposition */}
      <div className="space-y-1">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          What should happen?
        </p>
        <div className="flex gap-2">
          {(["rework", "drop"] as DeliverableDisposition[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onChange({ disposition: d })}
              className={cn(
                "flex-1 text-xs py-1.5 px-2 rounded-md border font-medium transition-colors",
                local.disposition === d
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background text-muted-foreground border-border hover:border-foreground/50",
              )}
              aria-pressed={local.disposition === d}
              data-testid={`button-disposition-${d}-${ad.itemAdId}`}
            >
              {d === "rework" ? "Use feedback going forward" : "Drop it"}
            </button>
          ))}
        </div>
      </div>

      {/* Save status hint */}
      {!complete ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`hint-reject-incomplete-${ad.itemAdId}`}>
          Select at least one reason and add a comment to save this rejection.
        </p>
      ) : local.saved ? (
        <p className="text-[11px] text-emerald-600 dark:text-emerald-500 flex items-center gap-1" data-testid={`hint-reject-saved-${ad.itemAdId}`}>
          <Check className="h-3 w-3" /> Saved
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`hint-reject-saving-${ad.itemAdId}`}>
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </p>
      )}
    </div>
  );
}

// ── Approval extras (optional tags + comment — persisted on the same row) ──

function ApproveExtras({
  ad,
  local,
  onChange,
}: {
  ad: DeliveryItemAdDto;
  local: LocalDecision;
  onChange: (patch: Partial<LocalDecision>) => void;
}) {
  const [open, setOpen] = useState(local.positiveTags.length > 0 || local.comment.trim().length > 0);
  return (
    <div className="space-y-1">
      <button
        type="button"
        className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        onClick={() => setOpen((o) => !o)}
        data-testid={`button-toggle-approve-extras-${ad.itemAdId}`}
      >
        <MessageSquare className="h-3 w-3" />
        {open ? "Hide approval comment" : "Add approval comment"}
      </button>
      {open && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30 p-3 space-y-2">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            What works well?
          </p>
          <div className="flex flex-wrap gap-1.5">
            {DELIVERABLE_POSITIVE_TAGS.map(({ key, label }) => {
              const active = local.positiveTags.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    onChange({
                      positiveTags: active
                        ? local.positiveTags.filter((t) => t !== key)
                        : [...local.positiveTags, key],
                    })
                  }
                  className={cn(
                    "text-[11px] px-2.5 py-1 rounded-full border font-medium transition-colors",
                    active
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-background text-muted-foreground border-border hover:border-emerald-400",
                  )}
                  aria-pressed={active}
                  data-testid={`chip-tag-${key}-${ad.itemAdId}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <Textarea
            value={local.comment}
            onChange={(e) => onChange({ comment: e.target.value })}
            placeholder="Share what you liked or add a note…"
            rows={2}
            maxLength={1000}
            className="text-sm resize-none"
            data-testid={`input-approve-comment-${ad.itemAdId}`}
          />
        </div>
      )}
    </div>
  );
}

// ── History drawer ─────────────────────────────────────────────────────────

function HistoryDrawer({
  decisions,
  adId,
}: {
  decisions: DeliveryItemDecisionDto[];
  adId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        onClick={() => setOpen((o) => !o)}
        data-testid={`button-history-${adId}`}
      >
        <History className="h-3 w-3" />
        {decisions.length} reviewer decision{decisions.length !== 1 ? "s" : ""}
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-l-2 border-border/50 pl-3">
          {decisions.map((d) => (
            <div key={d.id} className="text-[11px] space-y-0.5">
              <div className={`font-medium ${d.decision === "approved" ? "text-emerald-600 dark:text-emerald-500" : "text-destructive"}`}>
                {d.decision === "approved" ? "Approved by" : "Rejected by"}{" "}
                {d.authorLabel || "Reviewer"}
                {d.decision === "rejected" && d.disposition
                  ? ` · ${d.disposition === "drop" ? "Drop it" : "Use feedback going forward"}`
                  : ""}
              </div>
              {d.reasons.length > 0 && (
                <div className="text-muted-foreground">
                  {d.reasons.map((r) => {
                    const reason = DELIVERABLE_REJECT_REASONS.find((rr) => rr.key === r);
                    return reason?.label ?? r;
                  }).join(", ")}
                </div>
              )}
              {d.comment && (
                <p className="text-muted-foreground italic">"{d.comment}"</p>
              )}
              {d.createdAt && (
                <p className="text-muted-foreground/60">
                  {formatDistanceToNow(new Date(d.createdAt), { addSuffix: true })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Single ad review card ──────────────────────────────────────────────────

function AdReviewCard({
  ad,
  local,
  onApprove,
  onOpenReject,
  onChange,
  onUndo,
  onOpenLightbox,
  serverDecisions,
  isReadOnly,
  highlight,
  cardRef,
}: {
  ad: DeliveryItemAdDto;
  local: LocalDecision | null;
  onApprove: (itemAdId: string) => void;
  onOpenReject: (itemAdId: string) => void;
  onChange: (itemAdId: string, patch: Partial<LocalDecision>) => void;
  onUndo: (itemAdId: string) => void;
  onOpenLightbox: (itemAdId: string) => void;
  serverDecisions: DeliveryItemDecisionDto[];
  isReadOnly: boolean;
  highlight: boolean;
  cardRef?: (el: HTMLDivElement | null) => void;
}) {
  const resolved = resolveAdDecisionForAd(toDecisionLike(serverDecisions));

  // Editing overlay wins; otherwise the persisted server decision.
  const displayDecision = local?.decision ?? resolved.decision;
  const isApproved = displayDecision === "approved";
  const isRejected = displayDecision === "rejected";
  const isUnanswered = !displayDecision;
  // Show the reject panel while a local rejection edit is open (removed ads
  // and closed deliveries are read-only).
  const showRejectForm = !isReadOnly && !ad.removed && local?.decision === "rejected";

  const controlsDisabled = isReadOnly || ad.removed;

  return (
    <div
      ref={cardRef}
      className={cn(
        "deliverables-card rounded-xl border transition-all group overflow-hidden",
        isApproved && "border-emerald-300 dark:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-950/20",
        isRejected && "border-destructive/40 bg-destructive/5",
        isUnanswered && "border-border bg-card",
        ad.removed && "opacity-70",
        highlight && "ring-2 ring-primary",
      )}
      data-testid={`card-ad-${ad.itemAdId}`}
      tabIndex={controlsDisabled ? -1 : 0}
      aria-label={`${ad.displayName}: ${isApproved ? "Approved" : isRejected ? "Rejected" : "Unanswered"}${ad.removed ? " (removed)" : ""}`}
    >
      {/* Image */}
      <div
        className="relative overflow-hidden rounded-t-xl bg-[hsl(42_25%_95%)] cursor-zoom-in"
        onClick={() => onOpenLightbox(ad.itemAdId)}
      >
        <AdImage
          src={ad.imageUrl}
          alt={ad.displayName}
          thumbWidth={400}
          className="w-full object-cover aspect-video"
          data-testid={`img-ad-${ad.itemAdId}`}
        />
        {isApproved && (
          <div
            className="absolute top-2 right-2 bg-emerald-500 text-white rounded-full p-1.5 shadow"
            data-testid={`badge-approved-${ad.itemAdId}`}
          >
            <Check className="h-3.5 w-3.5" />
          </div>
        )}
        {isRejected && (
          <div
            className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1.5 shadow"
            data-testid={`badge-rejected-${ad.itemAdId}`}
          >
            <X className="h-3.5 w-3.5" />
          </div>
        )}
        {ad.removed && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white text-xs font-medium bg-black/60 px-2 py-1 rounded">
              Removed from library
            </span>
          </div>
        )}
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Eye className="h-4 w-4 text-white drop-shadow" />
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Name row */}
        <div className="flex items-start gap-2">
          <p
            className="text-sm font-medium flex-1 leading-snug"
            data-testid={`text-ad-name-${ad.itemAdId}`}
          >
            {ad.displayName}
          </p>
          {ad.aspectRatio && (
            <span className="text-[10px] text-muted-foreground border rounded px-1.5 py-0.5 shrink-0">
              {ad.aspectRatio}
            </span>
          )}
        </div>

        {/* Removed ads are read-only: show the recorded decision, no controls. */}
        {controlsDisabled && (
          <div className="space-y-1.5" data-testid={`readonly-decision-${ad.itemAdId}`}>
            {resolved.decision === "approved" && (
              <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                <span className="font-medium">Approved</span>
                {(() => {
                  const tags = (resolved.winningDecision as any)?.positiveTags as string[] | undefined;
                  return tags && tags.length > 0 ? (
                    <div className="flex gap-1 flex-wrap">
                      {tags.map((t: string) => {
                        const tag = DELIVERABLE_POSITIVE_TAGS.find((pt) => pt.key === t);
                        return tag ? (
                          <span key={t} className="text-[10px] bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full">
                            {tag.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  ) : null;
                })()}
              </div>
            )}
            {resolved.decision === "rejected" && resolved.winningDecision && (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-destructive text-sm">
                  <X className="h-4 w-4" />
                  <span className="font-medium">
                    Rejected · {resolved.winningDecision.disposition === "drop" ? "Drop it" : "Use feedback going forward"}
                  </span>
                </div>
                {resolved.winningDecision.reasons && resolved.winningDecision.reasons.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {resolved.winningDecision.reasons.map((r) => {
                      const reason = DELIVERABLE_REJECT_REASONS.find((rr) => rr.key === r);
                      return reason ? (
                        <span key={r} className="text-[10px] bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full">
                          {reason.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                {resolved.winningDecision.comment && (
                  <p className="text-xs text-muted-foreground italic">"{resolved.winningDecision.comment}"</p>
                )}
              </div>
            )}
            {!resolved.decision && (
              <p className="text-xs text-muted-foreground italic">No decision recorded</p>
            )}
          </div>
        )}

        {/* Live decision controls */}
        {!controlsDisabled && (
          <>
            {!showRejectForm && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={isApproved ? "default" : "outline"}
                  className={cn(
                    "flex-1 h-8 text-xs gap-1.5",
                    isApproved && "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
                  )}
                  onClick={() => {
                    if (isApproved) onUndo(ad.itemAdId);
                    else onApprove(ad.itemAdId);
                  }}
                  data-testid={`button-approve-${ad.itemAdId}`}
                >
                  {isApproved ? (
                    <><Check className="h-3.5 w-3.5" />Approved</>
                  ) : (
                    <><ThumbsUp className="h-3.5 w-3.5" />Approve</>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant={isRejected ? "destructive" : "outline"}
                  className="flex-1 h-8 text-xs gap-1.5"
                  onClick={() => {
                    if (isRejected) onUndo(ad.itemAdId);
                    else onOpenReject(ad.itemAdId);
                  }}
                  data-testid={`button-reject-${ad.itemAdId}`}
                >
                  <ThumbsDown className="h-3.5 w-3.5" />
                  {isRejected ? "Change" : "Reject"}
                </Button>
              </div>
            )}

            {/* Approve extras (only editable when there's a local edit) */}
            {isApproved && local && (
              <ApproveExtras
                ad={ad}
                local={local}
                onChange={(patch) => onChange(ad.itemAdId, patch)}
              />
            )}

            {/* Reject form */}
            {showRejectForm && local && (
              <RejectForm
                ad={ad}
                local={local}
                onChange={(patch) => onChange(ad.itemAdId, patch)}
                onClear={() => onUndo(ad.itemAdId)}
              />
            )}

            {/* Undo */}
            {displayDecision && (
              <button
                type="button"
                className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                onClick={() => onUndo(ad.itemAdId)}
                data-testid={`button-undo-${ad.itemAdId}`}
              >
                <RotateCcw className="h-3 w-3" />
                Undo
              </button>
            )}
          </>
        )}

        {/* History (other reviewers' decisions) */}
        {serverDecisions.length > 0 && (
          <HistoryDrawer decisions={serverDecisions} adId={ad.itemAdId} />
        )}
      </div>
    </div>
  );
}

// ── Project item section ───────────────────────────────────────────────────

function ProjectSection({
  item,
  decisions,
  localDecisions,
  onApprove,
  onOpenReject,
  onChange,
  onUndo,
  onOpenLightbox,
  isDeliveryClosed,
  focusedAdId,
  adCardRefs,
  filter,
}: {
  item: DeliveryItemDto;
  decisions: Map<string, DeliveryItemDecisionDto[]>;
  localDecisions: Map<string, LocalDecision>;
  onApprove: (itemAdId: string) => void;
  onOpenReject: (itemAdId: string) => void;
  onChange: (itemAdId: string, patch: Partial<LocalDecision>) => void;
  onUndo: (itemAdId: string) => void;
  onOpenLightbox: (itemAdId: string) => void;
  isDeliveryClosed: boolean;
  focusedAdId: string | null;
  adCardRefs: Map<string, (el: HTMLDivElement | null) => void>;
  filter: ReviewFilter;
}) {
  // Removed ads stay visible (read-only) but are excluded from the review flow.
  const activeAds = item.ads.filter((a) => !a.removed);
  const removedAds = item.ads.filter((a) => a.removed);

  const isProjectRejected = !!item.projectRejectedAt;
  const isSubmitted = !!item.submittedAt;

  // A rejected project short-circuits its per-ad UI — nothing to review until
  // the reviewer undoes the project rejection.
  const cards = isProjectRejected ? [] : activeAds.filter((ad) => {
    if (filter === "all") return true;
    const local = localDecisions.get(ad.itemAdId);
    const resolved = resolveAdDecisionForAd(toDecisionLike(decisions.get(ad.itemAdId) ?? []));
    const displayDecision = local?.decision ?? resolved.decision;
    if (filter === "unanswered") return !displayDecision;
    return displayDecision === filter;
  });

  return (
    <section className="space-y-4 rounded-2xl border border-border/60 bg-card/70 p-3 sm:p-5 shadow-sm" data-testid={`section-item-${item.id}`}>
      {/* Project header */}
      <div className="flex items-start justify-between gap-3 px-1">
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-snug" data-testid={`text-item-name-${item.id}`}>
            {item.projectNameSnapshot}
          </h2>
          {item.note && (
            <p className="text-sm text-muted-foreground mt-0.5" data-testid={`text-item-note-${item.id}`}>
              {item.note}
            </p>
          )}
          {isProjectRejected && (
            <div
              className="flex items-center gap-1.5 mt-1 text-sm text-destructive font-medium"
              data-testid={`badge-project-rejected-${item.id}`}
            >
              <X className="h-4 w-4" />
              Entire project rejected
              {item.projectRejectedByLabel && (
                <span className="font-normal">by {item.projectRejectedByLabel}</span>
              )}
              {item.projectRejectComment && (
                <span className="font-normal text-muted-foreground">· "{item.projectRejectComment}"</span>
              )}
            </div>
          )}
          {isSubmitted && !isProjectRejected && (
            <span className="text-[11px] text-emerald-600 dark:text-emerald-500 mt-1 inline-flex items-center gap-1" data-testid={`text-item-submitted-${item.id}`}>
              <CheckCircle2 className="h-3 w-3" />
              Submitted {formatDistanceToNow(new Date(item.submittedAt!), { addSuffix: true })}
              {!isDeliveryClosed && <span className="text-muted-foreground">· still editable</span>}
            </span>
          )}
        </div>
      </div>

      {/* Removed ads notice */}
      {removedAds.length > 0 && (
        <p
          className="text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2"
          data-testid={`notice-removed-ads-${item.id}`}
        >
          {removedAds.length} ad{removedAds.length !== 1 ? "s were" : " was"} removed from the ad library since this delivery was published. {removedAds.length !== 1 ? "They are" : "It is"} shown greyed out but no longer part of your review.
        </p>
      )}

      {!isProjectRejected && cards.length === 0 && activeAds.length > 0 && (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid={`empty-filtered-ads-${item.id}`}>
          No ads match this filter in this project.
        </p>
      )}

      {(cards.length > 0 || removedAds.length > 0) && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),22rem))] justify-start gap-5">
          {cards.map((ad) => (
            <AdReviewCard
              key={ad.itemAdId}
              ad={ad}
              local={localDecisions.get(ad.itemAdId) ?? null}
              onApprove={onApprove}
              onOpenReject={onOpenReject}
              onChange={onChange}
              onUndo={onUndo}
              onOpenLightbox={onOpenLightbox}
              serverDecisions={decisions.get(ad.itemAdId) ?? []}
              isReadOnly={isDeliveryClosed}
              highlight={focusedAdId === ad.itemAdId}
              cardRef={adCardRefs.get(ad.itemAdId)}
            />
          ))}
          {/* Removed ads are always read-only and shown at the end. */}
          {!isProjectRejected && (filter === "all") && removedAds.map((ad) => (
            <AdReviewCard
              key={ad.itemAdId}
              ad={ad}
              local={null}
              onApprove={onApprove}
              onOpenReject={onOpenReject}
              onChange={onChange}
              onUndo={onUndo}
              onOpenLightbox={onOpenLightbox}
              serverDecisions={decisions.get(ad.itemAdId) ?? []}
              isReadOnly
              highlight={false}
              cardRef={adCardRefs.get(ad.itemAdId)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ── Main review board ──────────────────────────────────────────────────────

const AUTOSAVE_DEBOUNCE_MS = 700;

class DeliverableRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "DeliverableRequestError";
  }
}

export default function DeliverableReviewPage() {
  const { brandId, deliveryId } = useParams<{ brandId: string; deliveryId: string }>();
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const { enabled, isUnknown } = useWeeklyDeliverablesEnabled(brandId);

  // ── Server data ──────────────────────────────────────────────────────────

  const {
    data: delivery,
    isLoading,
    isError,
    isRefetchError,
    error,
  } = useQuery<DeliveryDto>({
    queryKey: ["/api/brands", brandId, "deliveries", deliveryId],
    queryFn: async () => {
      const res = await fetch(
        `/api/brands/${brandId}/deliveries/${deliveryId}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        throw new DeliverableRequestError(res.status, `${res.status}: ${await res.text()}`);
      }
      return res.json();
    },
    enabled: !!brandId && !!deliveryId && !!user && enabled,
    staleTime: 3_000,
    refetchInterval: (query) =>
      query.state.data?.status === "closed" || query.state.data?.status === "revoked"
        ? false
        : 3_000,
    refetchIntervalInBackground: true,
    retry: (failureCount, requestError) =>
      !(requestError instanceof DeliverableRequestError && requestError.status === 404) &&
      failureCount < 2,
  });

  const deliveryBecameUnavailable =
    (isError || isRefetchError) &&
    error instanceof DeliverableRequestError &&
    error.status === 404;

  // ── Local editing state ──────────────────────────────────────────────────

  const [localDecisions, setLocalDecisions] = useState<Map<string, LocalDecision>>(new Map());
  // Keep every card in view by default. Reviewers commonly add comments, tags,
  // or pins immediately after deciding, so a new decision must not make its
  // card disappear unless they deliberately choose a narrower filter.
  const [filter, setFilter] = useState<ReviewFilter>("all");
  const [lightboxAdId, setLightboxAdId] = useState<string | null>(null);
  const [focusedAdId, setFocusedAdId] = useState<string | null>(null);

  // Submit confirmation targets ONE item.
  const [submitConfirmItemId, setSubmitConfirmItemId] = useState<string | null>(null);
  const [submitConfirmUnanswered, setSubmitConfirmUnanswered] = useState(0);

  // Approve remaining confirm targets ONE item.
  const [approveRemainingItemId, setApproveRemainingItemId] = useState<string | null>(null);

  // Debounce timers per ad (for rejection comment/reason autosave).
  const debounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Submit waits for every already-started autosave so its transactional
  // decision snapshot is always the final write.
  const pendingDecisionSaves = useRef<Set<Promise<unknown>>>(new Set());

  // Card refs for keyboard navigation.
  const adCardRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // ── Derived: read-only when the delivery is closed or revoked ─────────────
  const isDeliveryClosed = delivery?.status === "closed" || delivery?.status === "revoked";
  const isDeliveryRevoked = delivery?.status === "revoked";
  const isDraft = delivery?.status === "draft";

  // Flatten reviewable (non-removed) ads across all non-rejected items.
  const allAds = useMemo(() => {
    if (!delivery) return [];
    return delivery.items
      .filter((item) => !item.projectRejectedAt)
      .flatMap((item) => item.ads.filter((a) => !a.removed));
  }, [delivery]);

  // itemAdId → itemId lookup.
  const itemIdByAd = useMemo(() => {
    const map = new Map<string, string>();
    if (delivery) {
      for (const item of delivery.items) {
        for (const ad of item.ads) map.set(ad.itemAdId, item.id);
      }
    }
    return map;
  }, [delivery]);

  // itemAdId → server decisions (flat DTO preserved).
  const serverDecisionsByAd = useMemo<Map<string, DeliveryItemDecisionDto[]>>(() => {
    const map = new Map<string, DeliveryItemDecisionDto[]>();
    if (delivery) {
      for (const item of delivery.items) {
        for (const dec of item.decisions) {
          const existing = map.get(dec.deliveryItemAdId) ?? [];
          map.set(dec.deliveryItemAdId, [...existing, dec]);
        }
      }
    }
    return map;
  }, [delivery]);

  // Current user's own persisted decision for an ad, if any (reload recovery).
  const ownServerDecision = useCallback(
    (itemAdId: string): DeliveryItemDecisionDto | null => {
      const decs = serverDecisionsByAd.get(itemAdId) ?? [];
      return decs.find((d) => d.authorId && user && d.authorId === user.id) ?? null;
    },
    [serverDecisionsByAd, user],
  );

  // ── Autosave mutations ─────────────────────────────────────────────────

  const putDecision = useMutation({
    mutationFn: async ({ itemAdId, body }: { itemAdId: string; body: any }) => {
      const itemId = itemIdByAd.get(itemAdId);
      const res = await apiRequest(
        "PUT",
        `/api/brands/${brandId}/deliveries/${deliveryId}/items/${itemId}/ads/${itemAdId}/decision`,
        body,
      );
      return res.json();
    },
    onSuccess: (_data, vars) => {
      invalidateApprovalStates(qc, brandId);
      setLocalDecisions((prev) => {
        const cur = prev.get(vars.itemAdId);
        if (!cur) return prev;
        const next = new Map(prev);
        next.set(vars.itemAdId, { ...cur, saved: true });
        return next;
      });
      qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", "unreviewed-count"] });
    },
    onError: (e: any, vars) => {
      // Roll back the optimistic decision to the last server state.
      setLocalDecisions((prev) => {
        const next = new Map(prev);
        const own = ownServerDecision(vars.itemAdId);
        if (own) next.set(vars.itemAdId, localFromServer(own));
        else next.delete(vars.itemAdId);
        return next;
      });
      toast({
        title: "Couldn't save your decision",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteDecision = useMutation({
    mutationFn: async ({ itemAdId }: { itemAdId: string }) => {
      const itemId = itemIdByAd.get(itemAdId);
      const res = await apiRequest(
        "DELETE",
        `/api/brands/${brandId}/deliveries/${deliveryId}/items/${itemId}/ads/${itemAdId}/decision`,
      );
      return res.json().catch(() => ({}));
    },
    onSuccess: async () => {
      await Promise.all([
        invalidateApprovalStates(qc, brandId),
        qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", deliveryId] }),
        qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", "unreviewed-count"] }),
      ]);
    },
    onError: (e: any) => {
      // The local decision remains visible until DELETE succeeds, so a failed
      // undo needs no optimistic rollback.
      toast({
        title: "Couldn't undo your decision",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Persist a local decision now (approvals + complete rejections).
  const saveNow = useCallback(
    (itemAdId: string, local: LocalDecision) => {
      if (!isSavable(local)) return;
      let pending: Promise<unknown>;
      pending = putDecision
        .mutateAsync({ itemAdId, body: decisionBody(local) })
        .catch(() => undefined)
        .finally(() => pendingDecisionSaves.current.delete(pending));
      pendingDecisionSaves.current.add(pending);
    },
    [putDecision],
  );

  // Schedule a debounced save (used while typing a rejection comment).
  const scheduleSave = useCallback(
    (itemAdId: string, local: LocalDecision) => {
      const timers = debounceTimers.current;
      const existing = timers.get(itemAdId);
      if (existing) clearTimeout(existing);
      if (!isSavable(local)) return; // incomplete rejection stays local
      timers.set(
        itemAdId,
        setTimeout(() => {
          timers.delete(itemAdId);
          saveNow(itemAdId, local);
        }, AUTOSAVE_DEBOUNCE_MS),
      );
    },
    [saveNow],
  );

  // ── Decision actions ─────────────────────────────────────────────────────

  // Approve autosaves immediately (optimistic).
  const approve = useCallback(
    (itemAdId: string) => {
      const existing = localDecisions.get(itemAdId);
      const own = ownServerDecision(itemAdId);
      const base =
        existing?.decision === "approved"
          ? existing
          : own?.decision === "approved"
          ? localFromServer(own)
          : emptyLocal(itemAdId, "approved");
      const local: LocalDecision = { ...base, decision: "approved", saved: false };
      setLocalDecisions((prev) => new Map(prev).set(itemAdId, local));
      saveNow(itemAdId, local);
    },
    [localDecisions, ownServerDecision, saveNow],
  );

  // Open the reject panel (or seed from the reviewer's saved rejection).
  const openReject = useCallback(
    (itemAdId: string) => {
      const own = ownServerDecision(itemAdId);
      const seed =
        own?.decision === "rejected"
          ? localFromServer(own)
          : { ...emptyLocal(itemAdId, "rejected") };
      setLocalDecisions((prev) => new Map(prev).set(itemAdId, seed));
      setFocusedAdId(itemAdId);
    },
    [ownServerDecision],
  );

  // Edit a local decision. Approval edits (tags/comment) debounce-save the same
  // row. Reject edits debounce-save once complete; incomplete stays local.
  const changeDecision = useCallback(
    (itemAdId: string, patch: Partial<LocalDecision>) => {
      setLocalDecisions((prev) => {
        const cur = prev.get(itemAdId);
        if (!cur) return prev;
        const nextLocal: LocalDecision = { ...cur, ...patch, saved: false };
        const next = new Map(prev);
        next.set(itemAdId, nextLocal);
        scheduleSave(itemAdId, nextLocal);
        return next;
      });
    },
    [scheduleSave],
  );

  // Undo waits for any in-flight autosave, DELETEs unconditionally (the server
  // operation is idempotent), then clears local edit state. Looking only at the
  // stale delivery query can miss a row that was just created by autosave.
  const undo = useCallback(
    async (itemAdId: string) => {
      // Cancel any pending debounce for this ad.
      const t = debounceTimers.current.get(itemAdId);
      if (t) { clearTimeout(t); debounceTimers.current.delete(itemAdId); }

      try {
        await Promise.all([...pendingDecisionSaves.current]);
        await deleteDecision.mutateAsync({ itemAdId });
        setLocalDecisions((prev) => {
          const next = new Map(prev);
          next.delete(itemAdId);
          return next;
        });
      } catch {
        // deleteDecision.onError owns the user-facing error; keep the local
        // decision visible so the reviewer can retry.
      }
    },
    [deleteDecision],
  );

  // Approve remaining → one server call per item; optimistic UI.
  const approveRemainingMutation = useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/deliveries/${deliveryId}/items/${itemId}/approve-remaining`,
        {},
      );
      return res.json().catch(() => ({}));
    },
    onSuccess: () => {
      invalidateApprovalStates(qc, brandId);
      qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", deliveryId] });
      qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", "unreviewed-count"] });
    },
    onError: (e: any) => {
      // Refetch to reconcile optimistic UI against the server truth.
      qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", deliveryId] });
      toast({
        title: "Couldn't approve remaining ads",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const approveRemainingForItem = useCallback(
    (itemId: string) => {
      if (!delivery) return;
      const item = delivery.items.find((i) => i.id === itemId);
      if (!item) return;
      // Optimistically approve every unanswered non-removed ad in this item.
      setLocalDecisions((prev) => {
        const next = new Map(prev);
        for (const ad of item.ads.filter((a) => !a.removed)) {
          const local = prev.get(ad.itemAdId);
          const resolved = resolveAdDecisionForAd(toDecisionLike(serverDecisionsByAd.get(ad.itemAdId) ?? []));
          if (!local?.decision && !resolved.decision) {
            next.set(ad.itemAdId, { ...emptyLocal(ad.itemAdId, "approved"), saved: true });
          }
        }
        return next;
      });
      approveRemainingMutation.mutate({ itemId });
    },
    [delivery, serverDecisionsByAd, approveRemainingMutation],
  );

  // ── Submit ───────────────────────────────────────────────────────────────

  const submitItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      allowUnanswered,
      decisions,
    }: {
      itemId: string;
      allowUnanswered: boolean;
      decisions: SubmitDecisionPayload[];
    }) => {
      for (const timer of debounceTimers.current.values()) clearTimeout(timer);
      debounceTimers.current.clear();
      await Promise.all([...pendingDecisionSaves.current]);
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/deliveries/${deliveryId}/items/${itemId}/submit`,
        // Include the current local snapshot so submit and its decisions commit
        // atomically even when the reviewer beats the autosave debounce.
        { allowUnanswered, decisions },
      );
      return res.json().catch(() => ({}));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", deliveryId] });
      qc.invalidateQueries({ queryKey: ["/api/brands", brandId, "deliveries", "unreviewed-count"] });
      toast({ title: "Review submitted", description: "Thanks — your team has been notified." });
      invalidateApprovalStates(qc, brandId);
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't submit review",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Compute a single item's decision picture (local ⊕ server), for validation.
  const itemDecisionSummary = useCallback(
    (item: DeliveryItemDto) => {
      const activeAds = item.ads.filter((a) => !a.removed);
      const merged: DeliverableDecisionLike[] = [];
      const submitDecisions: SubmitDecisionPayload[] = [];
      let hasIncompleteReject = false;
      let unanswered = 0;
      for (const ad of activeAds) {
        const local = localDecisions.get(ad.itemAdId);
        if (local) {
          if (local.decision === "rejected" && !isRejectComplete(local)) {
            hasIncompleteReject = true;
          }
          merged.push({
            deliveryItemAdId: ad.itemAdId,
            decision: local.decision,
            reasons: local.decision === "rejected" ? local.reasons : [],
            comment: local.comment,
            disposition: local.decision === "rejected" ? local.disposition : null,
          });
          if (isSavable(local)) submitDecisions.push(submitDecisionBody(local));
          continue;
        }
        const resolved = resolveAdDecisionForAd(toDecisionLike(serverDecisionsByAd.get(ad.itemAdId) ?? []));
        if (resolved.winningDecision) {
          merged.push({
            deliveryItemAdId: ad.itemAdId,
            decision: resolved.winningDecision.decision,
            reasons: resolved.winningDecision.reasons ?? [],
            comment: resolved.winningDecision.comment ?? "",
            disposition: resolved.winningDecision.disposition ?? null,
          });
          submitDecisions.push({
            itemAdId: ad.itemAdId,
            decision: resolved.winningDecision.decision,
            reasons: (resolved.winningDecision.reasons ?? []) as DeliverableRejectReasonKey[],
            comment: resolved.winningDecision.comment ?? null,
            disposition: resolved.winningDecision.decision === "rejected"
              ? (resolved.winningDecision.disposition ?? "rework")
              : null,
            positiveTags: (resolved.winningDecision.positiveTags ?? []) as DeliverablePositiveTagKey[],
            pinX: typeof resolved.winningDecision.pinX === "number" ? resolved.winningDecision.pinX : null,
            pinY: typeof resolved.winningDecision.pinY === "number" ? resolved.winningDecision.pinY : null,
          });
        } else {
          unanswered += 1;
        }
      }
      return { activeAds, merged, submitDecisions, hasIncompleteReject, unanswered };
    },
    [localDecisions, serverDecisionsByAd],
  );

  const handleSubmit = useCallback(
    (itemId: string) => {
      if (!delivery) return;
      const item = delivery.items.find((i) => i.id === itemId);
      if (!item) return;

      const { activeAds, merged, submitDecisions, hasIncompleteReject, unanswered } = itemDecisionSummary(item);

      // Incomplete rejection: never submittable.
      if (hasIncompleteReject) {
        toast({
          title: "Finish your rejections first",
          description: "Every rejection needs at least one reason and a comment.",
          variant: "destructive",
        });
        return;
      }

      const validation = validateDeliverableSubmit({
        ads: activeAds.map((a) => ({ itemAdId: a.itemAdId })),
        decisions: merged,
        allowUnanswered: false,
      });

      if (!validation.ok) {
        if (validation.blockedAdIds.length > 0) {
          toast({
            title: "Finish your rejections first",
            description: validation.message,
            variant: "destructive",
          });
          return;
        }
        if (validation.unansweredAdIds.length > 0) {
          setSubmitConfirmItemId(itemId);
          setSubmitConfirmUnanswered(unanswered);
          return;
        }
      }

      submitItemMutation.mutate({ itemId, allowUnanswered: false, decisions: submitDecisions });
    },
    [delivery, itemDecisionSummary, submitItemMutation, toast],
  );

  // ── Summary stats (local ⊕ server, removed ads excluded) ─────────────────

  const totalAds = allAds.length;

  const summaryStats = useMemo(() => {
    let approved = 0;
    let rejected = 0;
    for (const ad of allAds) {
      const local = localDecisions.get(ad.itemAdId);
      const resolved = resolveAdDecisionForAd(toDecisionLike(serverDecisionsByAd.get(ad.itemAdId) ?? []));
      const displayDecision = local?.decision ?? resolved.decision;
      if (displayDecision === "approved") approved += 1;
      else if (displayDecision === "rejected") rejected += 1;
    }
    return { approved, rejected, unanswered: Math.max(0, totalAds - approved - rejected) };
  }, [allAds, localDecisions, serverDecisionsByAd, totalAds]);

  // ── Keyboard navigation + fast A/R ────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "TEXTAREA" ||
        target.tagName === "INPUT" ||
        target.isContentEditable
      ) return;
      if (lightboxAdId) return; // lightbox owns its keys
      if (isDeliveryClosed) return;

      if (e.key === "Escape") { setFocusedAdId(null); return; }

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        setFocusedAdId((cur) => {
          const idx = allAds.findIndex((a) => a.itemAdId === cur);
          const nxt = allAds[idx + 1] ?? allAds[0];
          if (nxt) { adCardRefs.current.get(nxt.itemAdId)?.focus(); return nxt.itemAdId; }
          return cur;
        });
        return;
      }
      if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        setFocusedAdId((cur) => {
          const idx = allAds.findIndex((a) => a.itemAdId === cur);
          const prv = allAds[idx - 1] ?? allAds[allAds.length - 1];
          if (prv) { adCardRefs.current.get(prv.itemAdId)?.focus(); return prv.itemAdId; }
          return cur;
        });
        return;
      }

      if (!focusedAdId) return;
      const ad = allAds.find((a) => a.itemAdId === focusedAdId);
      if (!ad || ad.removed) return;

      if (e.key === "a" || e.key === "A") {
        // Fast approve — autosaves.
        e.preventDefault();
        approve(focusedAdId);
        return;
      }
      if (e.key === "r" || e.key === "R") {
        // Fast reject — opens the panel; the ad is NOT saved until feedback is
        // complete (reasons + comment), matching the mouse flow.
        e.preventDefault();
        openReject(focusedAdId);
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [allAds, focusedAdId, isDeliveryClosed, lightboxAdId, approve, openReject]);

  // Clean up debounce timers on unmount.
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => { for (const t of timers.values()) clearTimeout(t); };
  }, []);

  // ── Lightbox data ────────────────────────────────────────────────────────

  const lightboxAds = useMemo(() => allAds.filter((a) => a.imageUrl), [allAds]);
  const lightboxStartIdx = useMemo(
    () => Math.max(0, lightboxAds.findIndex((a) => a.itemAdId === lightboxAdId)),
    [lightboxAds, lightboxAdId],
  );

  // ── Guard ────────────────────────────────────────────────────────────────

  if (authLoading || isUnknown) return null;
  if (!enabled) return <Redirect to={brandId ? `/brands/${brandId}` : "/"} />;

  // ── Loading ──────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),22rem))] justify-start gap-4">
          {[1, 2, 3, 4, 5, 6].map((n) => <Skeleton key={n} className="h-64 rounded-xl" />)}
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────

  if (deliveryBecameUnavailable) {
    return <Redirect to={`/brands/${brandId}/deliverables`} />;
  }

  if (isError || !delivery) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-6"
        data-testid="state-review-error"
      >
        <AlertTriangle className="h-10 w-10 text-destructive/70" />
        <p className="text-sm text-muted-foreground max-w-sm text-center">
          {(error as Error)?.message || "Could not load this delivery."}
        </p>
        <Button variant="outline" onClick={() => setLocation(`/brands/${brandId}/deliverables`)}>
          Back to deliveries
        </Button>
      </div>
    );
  }

  const progressPct = totalAds > 0
    ? Math.round(((summaryStats.approved + summaryStats.rejected) / totalAds) * 100)
    : 0;

  const showReviewChrome = !isDraft && !isDeliveryClosed && totalAds > 0;

  return (
    <div className="deliverables-review min-h-[100dvh] pb-32" data-testid="page-deliverable-review">
      {/* Top bar */}
      <div className="review-toolbar sticky top-0 z-20 backdrop-blur border-b px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setLocation(`/brands/${brandId}/deliverables`)}
            aria-label="Back to deliveries"
            data-testid="button-back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold truncate" data-testid="heading-delivery-title">
              {delivery.title}
            </h1>
            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              {delivery.dueAt && (
                <span className="flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" />
                  Due {format(new Date(delivery.dueAt), "MMM d, yyyy")}
                </span>
              )}
              {delivery.note && <span className="truncate max-w-xs">{delivery.note}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Draft notice */}
      {isDraft && (
        <div
          className="mx-4 sm:mx-6 mt-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3 flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400"
          data-testid="notice-draft"
        >
          <Clock className="h-4 w-4 shrink-0" />
          This delivery is still being prepared and is not yet published for review.
        </div>
      )}

      {/* Closed / thank-you notice */}
      {isDeliveryClosed && !isDeliveryRevoked && (
        <div
          className="mx-4 sm:mx-6 mt-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
          data-testid="notice-closed"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          This delivery has been closed. Your reviews are recorded below.
        </div>
      )}

      {/* Revoked notice */}
      {isDeliveryRevoked && (
        <div
          className="mx-4 sm:mx-6 mt-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 px-4 py-3 flex items-center gap-2 text-sm text-red-700 dark:text-red-400"
          data-testid="notice-revoked"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          This delivery has been revoked. Client access was removed. Internal history is preserved below.
        </div>
      )}

      {/* Filters */}
      {showReviewChrome && (
        <div className="px-4 sm:px-6 mt-5 flex items-center gap-2 flex-wrap">
          {(["all", "unanswered", "approved", "rejected"] as ReviewFilter[]).map((f) => {
            const count =
              f === "all" ? totalAds
              : f === "unanswered" ? summaryStats.unanswered
              : f === "approved" ? summaryStats.approved
              : summaryStats.rejected;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                 className={cn(
                   "text-xs px-3.5 py-2 rounded-full border font-medium transition-colors",
                  filter === f
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-muted-foreground border-border hover:border-foreground/40",
                )}
                data-testid={`filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {count > 0 && <span className="ml-1.5 opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {totalAds === 0 && !isDraft && (
        <div
          className="flex flex-col items-center justify-center min-h-[30vh] gap-3 p-6 text-center"
          data-testid="state-review-empty"
        >
          <Layers className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No ads in this delivery yet.</p>
        </div>
      )}

      {/* Item sections */}
      <div className="px-4 sm:px-6 mt-6 space-y-10">
        {delivery.items.map((item) => {
          const isItemSubmitted = !!item.submittedAt;
          const isProjectRejected = !!item.projectRejectedAt;
          const { unanswered } = itemDecisionSummary(item);

          return (
            <div key={item.id}>
              <ProjectSection
                item={item}
                decisions={serverDecisionsByAd}
                localDecisions={localDecisions}
                onApprove={approve}
                onOpenReject={openReject}
                onChange={changeDecision}
                onUndo={undo}
                onOpenLightbox={setLightboxAdId}
                isDeliveryClosed={isDeliveryClosed}
                focusedAdId={focusedAdId}
                adCardRefs={new Map(
                  item.ads.map((ad) => [
                    ad.itemAdId,
                    (el: HTMLDivElement | null) => { adCardRefs.current.set(ad.itemAdId, el); },
                  ]),
                )}
                filter={filter}
              />

              {/* Per-item actions (delivered / in-review, not closed) */}
              {!isDeliveryClosed && !isProjectRejected && item.ads.some((a) => !a.removed) && delivery.status !== "draft" && (
                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  {unanswered > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setApproveRemainingItemId(item.id)}
                      disabled={approveRemainingMutation.isPending}
                      data-testid={`button-approve-remaining-${item.id}`}
                    >
                      <SkipForward className="h-3 w-3 mr-1" />
                      Approve remaining ({unanswered})
                    </Button>
                  )}
                  <Button
                    onClick={() => handleSubmit(item.id)}
                    disabled={submitItemMutation.isPending}
                    className="gap-1.5"
                    data-testid={`button-submit-item-${item.id}`}
                  >
                    {submitItemMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    {isItemSubmitted ? "Re-submit review" : unanswered > 0 ? `Submit review (${unanswered} unanswered)` : "Submit review"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sticky progress footer */}
      {showReviewChrome && (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur border-t border-border/50 px-4 sm:px-6 py-3"
          data-testid="footer-progress"
        >
          <div className="max-w-4xl mx-auto flex items-center gap-4 rounded-xl">
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{summaryStats.approved + summaryStats.rejected} of {totalAds} reviewed</span>
                <span>{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="h-1.5" />
            </div>
            <div className="flex items-center gap-2 text-xs shrink-0">
              {summaryStats.approved > 0 && (
                <span className="text-emerald-600 dark:text-emerald-500 font-medium">{summaryStats.approved} approved</span>
              )}
              {summaryStats.rejected > 0 && (
                <span className="text-destructive font-medium">{summaryStats.rejected} rejected</span>
              )}
              {summaryStats.unanswered > 0 && (
                <span className="text-muted-foreground">{summaryStats.unanswered} pending</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxAdId && lightboxAds.length > 0 && (
        <Lightbox
          ads={lightboxAds}
          startIndex={lightboxStartIdx}
          onClose={() => setLightboxAdId(null)}
          onPin={(itemAdId, x, y) => {
            const cur = localDecisions.get(itemAdId);
            if (cur) changeDecision(itemAdId, { pinX: x, pinY: y });
          }}
        />
      )}

      {/* Submit unanswered confirmation — targets ONE item */}
      <AlertDialog
        open={!!submitConfirmItemId}
        onOpenChange={(o) => { if (!o) setSubmitConfirmItemId(null); }}
      >
        <AlertDialogContent data-testid="dialog-submit-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit with unanswered ads?</AlertDialogTitle>
            <AlertDialogDescription>
              {submitConfirmUnanswered} ad{submitConfirmUnanswered !== 1 ? "s have" : " has"} no answer yet in this project. You can submit now and review the rest later, or go back and finish first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-submit-confirm-cancel">
              Go back and review
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const itemId = submitConfirmItemId;
                setSubmitConfirmItemId(null);
                const item = delivery?.items.find((candidate) => candidate.id === itemId);
                if (itemId && item) {
                  const current = itemDecisionSummary(item);
                  if (!current.hasIncompleteReject) {
                    submitItemMutation.mutate({
                      itemId,
                      allowUnanswered: true,
                      decisions: current.submitDecisions,
                    });
                  }
                }
              }}
              data-testid="button-submit-confirm-proceed"
            >
              Submit anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve remaining confirmation — targets ONE item */}
      <AlertDialog
        open={!!approveRemainingItemId}
        onOpenChange={(o) => { if (!o) setApproveRemainingItemId(null); }}
      >
        <AlertDialogContent data-testid="dialog-approve-remaining-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve all remaining ads?</AlertDialogTitle>
            <AlertDialogDescription>
              This will approve every unanswered ad in this project. You can still change individual decisions afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-approve-remaining-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const itemId = approveRemainingItemId;
                setApproveRemainingItemId(null);
                if (itemId) approveRemainingForItem(itemId);
              }}
              data-testid="button-approve-remaining-confirm"
            >
              Approve remaining
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
